<template>
  <v-dialog persistent v-model="showDialog" content-class="verify-code-dialog col-xs-12 col-sm-10 col-md-4 col-lg-4">
    <v-overlay :value="showLoader">
        <v-progress-circular 
            indeterminate color="primary"
            :size="70" :width="7"
        ></v-progress-circular>
    </v-overlay>
    <v-card class="verify-modal">
      <div class="verify-close-container">
        <v-btn icon @click="onClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="verify-content">
        <v-alert
          class="mb-2 mt-1"
          v-if="errorMessage"
          :type="'error'">
          {{ errorMessage }}
        </v-alert>
        <h1 class="mt-3 mb-3">Enter the code you've received</h1>
        <p>
          The code will be sent to {{verificationTypeDescription}}<br/>
          <strong>Please, wait a couple of minutes to receive the code before asking for a new one.</strong>
          You have {{verificationTypeTimeoutDescription}} and 3 attempts to enter the correct code.<br/>
        </p>
        <p v-if="!!infoMessage">{{infoMessage}}</p>
        <v-text-field
          label="Verification code"
          v-model="verificationCode"
          type="number"
          />
        <v-btn
          v-if="!challengeId"
          :disabled="showLoader || resendCooldownSeconds > 0"
          large
          plain
          color="primary"
          v-on:click="resendVerificationCode"
          ><u>{{ resendButtonText }}</u></v-btn>
        <v-btn
          :disabled="!verificationCode"
          large
          color="primary"
          v-on:click="checkCode"
          >{{text}}</v-btn>
      </div>
    </v-card>
  </v-dialog> 
</template>


<script lang="ts">
import Vue from "vue";
import { UserInfo } from "@/types/User";
import codeVerificationService, { VerificationType } from "@/services/codeVerificationService";
import { getErrorMessage } from "@/services/errorHandler";

type VerificationHttpResponse = {
  data?: unknown;
  status?: number;
  headers?: Record<string, unknown> & { get?: (name: string) => unknown };
};

const verificationHttpResponse = (error: unknown): VerificationHttpResponse => {
  const candidate = error as VerificationHttpResponse & { response?: VerificationHttpResponse };
  return candidate?.response ?? candidate ?? {};
};

const verificationErrorMessage = (error: unknown): string => {
  const responseData = verificationHttpResponse(error).data;
  return typeof responseData === "string" ? responseData : getErrorMessage(error);
};

const verificationRetryAfter = (error: unknown): number => {
  const response = verificationHttpResponse(error);
  if (response.status !== 429) return 0;

  const retryAfter = response.headers?.get?.("retry-after")
    ?? response.headers?.["retry-after"];
  const retryAfterSeconds = Number(retryAfter);
  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? Math.ceil(retryAfterSeconds)
    : 60;
};

export default Vue.extend({
  name: "VerifyCodeDialog",
  props: {
    buttonText: {type: String},
    showDialog: {type: Boolean},
    verificationType: {type: Number},
    contactInfo: {type: String},
    onSuccess: {type: Function},
    onClose: {type: Function},
    challengeId: {type: String, default: ""},
    onVerify: {type: Function},
  },
  data: function() {
    return {
      text: this.buttonText ?? "Verify",
      errorMessage: "",
      infoMessage: "",
      showLoader: false,
      verificationGuid: "",
      verificationGuids: [],
      verificationCode: "",
      resendCooldownSeconds: 0,
      resendCooldownEndsAt: 0,
      resendTimer: null as number | null,
      verificationRequestVersion: 0,
    };
  },
  watch: {
    "showDialog": function(newVal, oldVal){
      if (newVal === oldVal) return;

      if (newVal === true) {
        this.verificationCode = "";
        if (!this.challengeId && !this.verificationGuid && !this.verificationGuids.length) {
          this.sendVerificationCode();
        }
      }
    },
    contactInfo: function(newVal, oldVal) {
      if (newVal === oldVal) return;

      this.resetVerification();
      if (this.showDialog && !this.challengeId) this.sendVerificationCode();
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    verificationTypeTimeoutDescription(): String {
      switch(this.verificationType) {
        case VerificationType.Email:
        case VerificationType.PhoneNumber:
        default:
          return "30 minutes";
      }
    },
    verificationTypeDescription(): String {
      switch(this.verificationType) {
        case VerificationType.Email:
          return "the email address provided.";
        case VerificationType.PhoneNumber:
          return "the phone number provided.";
        default:
          return "";
      }
    },
    sendCodeFun(): Function {
      switch(this.verificationType) {
        case VerificationType.Email:
          return codeVerificationService.startEmailVerification;
        case VerificationType.PhoneNumber:
          return codeVerificationService.startPhoneNumberVerification;
        default:
          return () => {};
      }
    },
    resendButtonText(): string {
      return this.resendCooldownSeconds > 0
        ? `Resend code in ${this.resendCooldownSeconds}s`
        : "Resend code";
    },
  }, 
  beforeDestroy() {
    if (this.resendTimer !== null) window.clearInterval(this.resendTimer);
  },
  methods: {
    resendVerificationCode: function() {
      this.sendVerificationCode();
    },
    resetVerification: function() {
      this.verificationRequestVersion++;
      if (this.resendTimer !== null) window.clearInterval(this.resendTimer);
      this.resendTimer = null;
      this.resendCooldownSeconds = 0;
      this.resendCooldownEndsAt = 0;
      this.verificationGuid = "";
      this.verificationGuids = [];
      this.verificationCode = "";
      this.errorMessage = "";
      this.infoMessage = "";
      this.showLoader = false;
    },
    startResendCooldown: function(seconds = 60) {
      if (this.resendTimer !== null) window.clearInterval(this.resendTimer);
      this.resendCooldownSeconds = seconds;
      this.resendCooldownEndsAt = Date.now() + (seconds * 1000);
      this.resendTimer = window.setInterval(() => {
        this.resendCooldownSeconds = Math.max(
          0,
          Math.ceil((this.resendCooldownEndsAt - Date.now()) / 1000)
        );
        if (this.resendCooldownSeconds <= 0 && this.resendTimer !== null) {
          window.clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }, 1000);
    },
    sendVerificationCode: async function() {
      if (this.showLoader) return;

      const requestVersion = ++this.verificationRequestVersion;
      this.showLoader = true;
      try {
        const res = await this.sendCodeFun(this.contactInfo);
        if (requestVersion !== this.verificationRequestVersion) return;

        if (res.success) {
          this.verificationGuid = res.instanceId;
          this.verificationGuids = res.instanceIds ?? [];
          this.verificationCode = "";
          this.errorMessage = "";
          this.infoMessage = res.infoMessage ?? "";
          this.startResendCooldown();
        } else if (res.errors) {
          this.errorMessage = res.errors.join(",");
        }
      } catch (error) {
        if (requestVersion !== this.verificationRequestVersion) return;

        this.errorMessage = verificationErrorMessage(error);
        const retryAfter = verificationRetryAfter(error);
        if (retryAfter > 0) this.startResendCooldown(retryAfter);
      } finally {
        if (requestVersion === this.verificationRequestVersion) this.showLoader = false;
      }
    },
    checkCode: async function() {
      const requestVersion = this.verificationRequestVersion;
      this.showLoader = true;

      if (this.challengeId && this.onVerify) {
        try {
          const response = await this.onVerify(this.challengeId, this.verificationCode);
          if (requestVersion !== this.verificationRequestVersion) return;

          if (response.success) {
            this.onSuccess(response);
            this.errorMessage = "";
            this.verificationCode = "";
          } else {
            this.errorMessage = response.errorMessage;
          }
        } catch (error) {
          if (requestVersion !== this.verificationRequestVersion) return;
          this.errorMessage = "The code could not be verified. Please try again.";
        } finally {
          if (requestVersion === this.verificationRequestVersion) this.showLoader = false;
        }
        return;
      }

      if (!this.verificationGuid && !this.verificationGuids.length) 
      {
        this.showLoader = false;
        this.errorMessage = "Start verification failed";
        return;
      }

      if (!!this.verificationGuid || this.verificationGuids.length === 1) {
        const guid = this.verificationGuid ?? this.verificationGuids[0];

        try {
          const response = await codeVerificationService.postVerificationCodeForChallengeType(guid, this.verificationCode, this.verificationType);
          if (requestVersion !== this.verificationRequestVersion) return;

          if (response.CodeCorrect) {
            this.onSuccess(guid);
            this.errorMessage = "";
            this.verificationCode = "";
            this.verificationGuid = "";
            this.verificationGuids = [];
          } else this.errorMessage = response.ErrorMessage;
        } catch (error) {
          if (requestVersion !== this.verificationRequestVersion) return;
          this.errorMessage = verificationErrorMessage(error);
          const retryAfter = verificationRetryAfter(error);
          if (retryAfter > 0) this.startResendCooldown(retryAfter);
        } finally {
          if (requestVersion === this.verificationRequestVersion) this.showLoader = false;
        }
      } else {
        let success = false;
        let errorMessage = "";
        let lastError: unknown = null;

        let successfulGuid = "";
        for (const guid of this.verificationGuids) {
          try {
            const response = await codeVerificationService.postVerificationCodeForChallengeType(guid, this.verificationCode, this.verificationType);
            if (requestVersion !== this.verificationRequestVersion) return;

            if (response.CodeCorrect == true) {
              success = true;
              successfulGuid = guid;
              break; // No further checks are needed;
            } else errorMessage = response.ErrorMessage;
          } catch (error) {
            if (requestVersion !== this.verificationRequestVersion) return;
            lastError = error;
          }
        }
        
        if (success) {
          this.onSuccess(successfulGuid);
          this.errorMessage = "";
          this.verificationCode = "";
          this.verificationGuid = "";
          this.verificationGuids = [];
        } else if (errorMessage) {
          this.errorMessage = errorMessage;
        } else if (lastError) {
          this.$handleError(lastError);
        }

        if (requestVersion === this.verificationRequestVersion) this.showLoader = false;
      }
    },
  },
});
</script>
